import React from "react";
import {
  Breadcrumbs as MUIBreadCrumbs,
  Link,
  makeStyles,
} from "@material-ui/core";
import clsx from "clsx";
import { Link as RouterLink } from "react-router-dom";
import { logger } from "@origin-digital/reporting-client";
import { PathnameMap } from "../../interfaces/breadcrumbs";

export interface IBreadCrumbsProps {
  pathnameMap: PathnameMap;
  pathname: string;
}

const useStyles = makeStyles((theme) => ({
  baseLink: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.fontSize,
  },
  inactive: {
    "&:hover, &:focus": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  },
  active: {
    color: theme.palette.grey[600],
    "&:hover, &:focus": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  },
  static: {
    color: theme.palette.grey[600],
    "&:hover, &:focus": {
      textDecoration: "none",
    },
  },
}));

const LinkRouter = (props) => <Link {...props} component={RouterLink} />;

export const Breadcrumbs = ({
  pathnameMap,
  pathname,
}: IBreadCrumbsProps): JSX.Element | null => {
  const pathnames = pathname.split("/").filter((x) => x !== "");
  let first = true;
  const elements: JSX.Element[] = [];
  const classes = useStyles();

  const generateElements = (index = 0) => {
    if (index === pathnames.length) return null;

    generateElements(index + 1);

    const to = `/${pathnames.slice(0, index + 1).join("/")}`;
    const matchedPath = first && pathname === to;

    if (!pathnameMap[to]) {
      logger.warn(`Cannot find path ${to} in map. Returning NULL`);
      return null;
    }
    const props = {
      key: to,
      "data-id": `crumb-${pathnames[index]}`,
      to,
      color: "inherit",
    };

    const label = pathnameMap[to];
    const styles = clsx([
      classes.baseLink,
      matchedPath && classes.static,
      !matchedPath && first && classes.active,
      !matchedPath && !first && classes.inactive,
    ]);

    if (matchedPath || first) first = false;
    logger.debug(`Inserting breadcrumb for path ${to}.`);
    elements.unshift(
      <LinkRouter className={styles} {...props}>
        {label}
      </LinkRouter>
    );
    return undefined;
  };

  generateElements();

  return (
    <MUIBreadCrumbs aria-label="breadcrumb" data-id="breadcrumb" separator="/">
      {elements}
    </MUIBreadCrumbs>
  );
};
